import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, MapPin, User, AlertTriangle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const CATEGORY_LABEL = {
  IBADAH_KLASIS: "Ibadah Klasis", IBADAH_JEMAAT: "Ibadah Jemaat",
  RAPAT: "Rapat", SIDANG: "Sidang", RAKER: "RAKER", KKR: "KKR",
  PELATIHAN: "Pelatihan", KUNJUNGAN: "Kunjungan",
  PERIA: "PERIA", PERNITA: "PERNITA", PEREDA: "PEREDA", PERSEMI: "PERSEMI",
  PERTUKARAN: "Pertukaran Pelayan", HARI_BESAR: "Hari Besar",
};

const CATEGORY_COLOR = {
  IBADAH_KLASIS: "bg-primary/15 text-primary border-primary/30",
  IBADAH_JEMAAT: "bg-emerald-100 text-emerald-800 border-emerald-200",
  RAPAT: "bg-sky-100 text-sky-800 border-sky-200",
  SIDANG: "bg-purple-100 text-purple-800 border-purple-200",
  RAKER: "bg-indigo-100 text-indigo-800 border-indigo-200",
  KKR: "bg-secondary/15 text-secondary border-secondary/30",
  PELATIHAN: "bg-amber-100 text-amber-800 border-amber-200",
  KUNJUNGAN: "bg-stone-100 text-stone-700 border-stone-200",
};

function pad(n) { return String(n).padStart(2, "0"); }
function iso(y, m, d) { return `${y}-${pad(m+1)}-${pad(d)}`; }

export default function Kalender() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "IBADAH_KLASIS", date: new Date().toISOString().slice(0,10), time: "", location: "", pic: "", is_public: false, description: "" });
  const [conflicts, setConflicts] = useState([]);

  const canCreate = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS","KOORDINATOR_BIDANG","SEKRETARIS_JEMAAT","GEMBALA_JEMAAT"].includes(user?.role);

  const load = () => { setLoading(true); api.get("/events").then((r)=>setItems(r.data)).finally(()=>setLoading(false)); };
  useEffect(() => { load(); }, []);

  // Build calendar grid
  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const lastDay = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const startWeekday = first.getDay(); // 0=Sun
    const cells = [];
    // fill prev month blanks
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) cells.push(d);
    // pad end
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    items.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [items]);

  // Conflicts: same date, same location, or same PIC
  const conflictDates = useMemo(() => {
    const dateGroups = {};
    items.forEach((e) => { (dateGroups[e.date] = dateGroups[e.date] || []).push(e); });
    const dates = new Set();
    Object.entries(dateGroups).forEach(([d, list]) => {
      if (list.length < 2) return;
      for (let i = 0; i < list.length; i++)
        for (let j = i+1; j < list.length; j++) {
          const a = list[i], b = list[j];
          if (a.time && b.time && a.time === b.time) dates.add(d);
          else if (a.location && a.location === b.location && a.time && b.time && Math.abs(parseInt(a.time)-parseInt(b.time)) < 2) dates.add(d);
        }
    });
    return dates;
  }, [items]);

  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const dow = ["Min","Sen","Sel","Rab","Kam","Jum","Sab"];
  const today = new Date();

  const submit = async () => {
    if (!form.title || !form.date) { toast.error("Judul & tanggal wajib"); return; }
    try {
      const { data } = await api.post("/events", form);
      if (data.conflicts && data.conflicts.length > 1) {
        setConflicts(data.conflicts.filter(c => c.id !== data.event.id));
        toast.warning(`Agenda dibuat, tapi ada ${data.conflicts.length - 1} konflik jadwal.`);
      } else {
        toast.success("Agenda berhasil dibuat.");
      }
      setOpen(false);
      setForm({ title: "", category: "IBADAH_KLASIS", date: new Date().toISOString().slice(0,10), time: "", location: "", pic: "", is_public: false, description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Pelayanan</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Kalender Pelayanan</h1>
          <p className="text-sm text-muted-foreground mt-2">Agenda ibadah, rapat, dan pelayanan Klasis Mimika. Deteksi konflik otomatis untuk jadwal bersamaan.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-event"><Plus className="h-4 w-4 mr-2" /> Agenda Baru</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Agenda Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Judul</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="input-event-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Kategori</Label>
                    <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                      <SelectTrigger data-testid="select-event-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORY_LABEL).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="input-event-date" /></div>
                  <div><Label>Jam</Label><Input type="time" value={form.time} onChange={(e)=>setForm({...form, time:e.target.value})} data-testid="input-event-time" /></div>
                  <div><Label>PIC</Label><Input value={form.pic} onChange={(e)=>setForm({...form, pic:e.target.value})} data-testid="input-event-pic" /></div>
                </div>
                <div><Label>Lokasi</Label><Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} data-testid="input-event-location" /></div>
                <div><Label>Deskripsi</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} /></div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.is_public} onCheckedChange={(v)=>setForm({...form, is_public:!!v})} data-testid="checkbox-event-public" />
                  <span className="text-sm">Tampilkan di Portal Publik</span>
                </label>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="btn-save-event">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {conflicts.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <div className="text-sm">
            <strong>Deteksi konflik jadwal:</strong> agenda baru bertabrakan dengan {conflicts.length} agenda lain.
            <ul className="mt-1 text-xs list-disc pl-4">
              {conflicts.map((c) => <li key={c.id}>{c.title} — {c.date} {c.time || ""} @ {c.location || "—"}</li>)}
            </ul>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="month" data-testid="tab-month">Bulan</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list">Daftar</TabsTrigger>
          </TabsList>
        </Tabs>
        {view === "month" && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={()=>setCursor((c)=>({ y: c.m === 0 ? c.y-1 : c.y, m: c.m === 0 ? 11 : c.m-1 }))} data-testid="btn-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="font-heading font-bold text-sm w-40 text-center capitalize" data-testid="month-label">{monthLabel}</div>
            <Button variant="outline" size="icon" onClick={()=>setCursor((c)=>({ y: c.m === 11 ? c.y+1 : c.y, m: c.m === 11 ? 0 : c.m+1 }))} data-testid="btn-next-month"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={()=>{ const d=new Date(); setCursor({y:d.getFullYear(),m:d.getMonth()}); }} data-testid="btn-today">Hari ini</Button>
          </div>
        )}
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : view === "month" ? (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <div className="grid grid-cols-7 border-b border-border">
              {dow.map((d) => <div key={d} className="text-[0.65rem] tracking-[0.15em] uppercase text-muted-foreground text-center py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {grid.map((day, i) => {
                if (day == null) return <div key={i} className="min-h-[110px] border border-border/40 bg-muted/20" />;
                const date = iso(cursor.y, cursor.m, day);
                const events = eventsByDate[date] || [];
                const isToday = date === today.toISOString().slice(0,10);
                const conflict = conflictDates.has(date);
                return (
                  <div key={i} className={`min-h-[110px] border border-border/40 p-1.5 ${isToday ? "bg-primary/5" : "bg-background"}`} data-testid={`cell-${date}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className={`text-xs font-semibold ${isToday ? "text-primary" : "text-foreground"}`}>{day}</div>
                      {conflict && <AlertTriangle className="h-3 w-3 text-destructive" title="Konflik jadwal" />}
                    </div>
                    <div className="space-y-1">
                      {events.slice(0,3).map((e) => (
                        <div key={e.id} className={`text-[0.65rem] px-1.5 py-1 rounded border truncate ${CATEGORY_COLOR[e.category] || "bg-stone-100 text-stone-700 border-stone-200"}`} title={`${e.title} ${e.time || ""}`}>
                          {e.time && <span className="font-mono opacity-70 mr-1">{e.time}</span>}{e.title}
                        </div>
                      ))}
                      {events.length > 3 && <div className="text-[0.6rem] text-muted-foreground">+{events.length - 3} lagi</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground italic">Belum ada agenda.</div>}
          {items.map((e) => (
            <Card key={e.id} data-testid={`event-list-${e.id}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-16 shrink-0 text-center border-r border-border pr-4">
                  <div className="text-[0.65rem] uppercase text-muted-foreground">{new Date(e.date).toLocaleDateString('id-ID',{weekday:'short'})}</div>
                  <div className="font-heading font-bold text-2xl text-primary leading-none mt-1">{new Date(e.date).getDate()}</div>
                  <div className="text-[0.65rem] text-muted-foreground mt-1">{new Date(e.date).toLocaleDateString('id-ID',{month:'short'})}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`status-badge ${CATEGORY_COLOR[e.category] || ""}`}>{CATEGORY_LABEL[e.category] || e.category}</span>
                    {e.is_public && <StatusBadge status="success" label="Publik" />}
                    {conflictDates.has(e.date) && <StatusBadge status="destructive" label="KONFLIK" />}
                  </div>
                  <div className="font-heading font-bold">{e.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location || "—"}</span>
                    {e.time && <span className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> {e.time}</span>}
                    {e.pic && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {e.pic}</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
